import { Fragment, type ReactNode } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@eureca/ui';
import {
  ConsultantCopyableId,
  ConsultantLayout,
  ConsultantListHeader,
  ConsultantListPlaceholder,
  ConsultantPageTitle,
  ConsultantSearchInput,
  ConsultantSortDropdown,
  ConsultantTablePagination,
  ConsultantTableSection,
  type ConsultantSortOption,
} from '@/shared/consultant-layout';
import {
  ProgramPublishDialog,
  type PublishDialogIncompleteJob,
  type PublishVisibility,
} from './ProgramPublishDialog';
import { ProgramConfirmDialog } from './ProgramConfirmDialog';
import {
  ArchiveIcon,
  BanIcon,
  FilePenIcon,
  FlagIcon,
  LoaderCircleIcon,
  MoreHorizontalIcon,
  PlusIcon,
  RadioIcon,
  TriangleAlertIcon,
} from './icons';
import {
  getExportGroup,
  getMenuGroupsForStatus,
  type ProgramAction,
  type ProgramBaseType as ProgramBaseTypeOption,
  type ProgramStatus,
} from '../program-actions-menu';
import { formatProgramRegistrations } from './program-row-format';

export type { ProgramStatus, ProgramAction };

export interface Program {
  /** Real UUID — used as the row key, shown in the ID column, and passed
   *  to action handlers. */
  id: string;
  name: string;
  /** EU-241 — company logo URL; `null` falls back to a placeholder icon. */
  logoUrl: string | null;
  status: ProgramStatus;
  /** Coluna "Vagas" — `jobsCount` é o que o Core devolve hoje (uma vaga
   *  agrupa N vagas; ver eureca-core); o label "Vagas" reflete o que o
   *  consultor pensa quando olha pra lista. */
  jobsCount: number;
  registrationsCount: number;
  /** Coluna "Início" — `start_applying` formatado dd/MM/yy ou `—`. */
  startDate: string;
  /** Coluna "Fim" — `end_applying` formatado dd/MM/yy ou `—`. */
  endDate: string;
  /**
   * EU-405 — dias restantes até o fim do prazo (`endApplying`), contagem
   * inclusiva BRT recalculada por dia. `null` para todos os status exceto
   * Ativa / Ativo e privado (sem badge). Ver `program-row-format.ts`.
   */
  daysRemaining: number | null;
}

export type ProgramActiveDialog = 'publish' | 'end' | 'cancel' | null;

export interface ProgramsPageProps {
  programs: Program[];
  /** Workspace logo node (selected company) rendered next to the page title. */
  brand?: ReactNode;
  isInitialLoading: boolean;
  isReordering: boolean;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateProgram: () => void;
  /**
   * Sort dropdown items built by the container from the canonical 4-option
   * list (`programs-state.ts`), matching the Empresas listing layout.
   * Each entry's `isSelected` flags the active one for the dropdown render.
   */
  sortOptions: { id: string; label: string; isSelected: boolean }[];
  /** Active option label — feeds the dropdown's "Ordenação atual: …" aria. */
  selectedSortLabel?: string;
  /** Mirrors the dropdown's pressed-state styling — true when any option is active. */
  isSortSelected: boolean;
  /** Fires with the option's `id` when the consultor picks one. */
  onSelectSort: (id: string) => void;
  /** Fires when the consultor clicks "Remover ordenação" inside the dropdown. */
  onClearSort: () => void;
  onOpenProgram: (id: string) => void;
  onProgramAction: (action: ProgramAction, id: string) => void;
  /**
   * EU-654 RN-02/03 — a seção "Extrair bases" do menu da linha é DINÂMICA:
   * o container busca a disponibilidade no BFF quando o menu abre (um menu
   * por vez). `exportMenuTypes` undefined = carregando.
   */
  exportMenuProgramId: string | null;
  exportMenuTypes: ProgramBaseTypeOption[] | undefined;
  onRowMenuOpenChange: (programId: string, open: boolean) => void;
  rowsPerPage: number;
  rowsPerPageOptions: number[];
  onRowsPerPageChange: (rows: number) => void;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  activeDialog: ProgramActiveDialog;
  onDialogOpenChange: (open: boolean) => void;
  publishVisibility: PublishVisibility;
  onPublishVisibilityChange: (visibility: PublishVisibility) => void;
  onConfirmPublish: () => void;
  onConfirmEnd: () => void;
  onConfirmCancel: () => void;
  /**
   * EU-450 — "Preencha completamente as vagas para publicar" when the
   * publish-readiness pre-check finds incomplete jobs. Blocks Publicar.
   */
  publishError?: string;
  /** EU-450 — the incomplete jobs, rendered as new-tab links in the dialog. */
  publishIncompleteJobs?: PublishDialogIncompleteJob[];
  /** True while the readiness pre-check is in flight. */
  isCheckingPublishReadiness?: boolean;
}

export type ProgramSortKey = 'name' | 'status' | 'createdAt' | 'updatedAt';
export type ProgramSortDir = 'asc' | 'desc';

export function ProgramsPage({
  programs,
  brand,
  isInitialLoading,
  isReordering,
  searchQuery,
  onSearchChange,
  onCreateProgram,
  sortOptions,
  selectedSortLabel,
  isSortSelected,
  onSelectSort,
  onClearSort,
  onOpenProgram,
  onProgramAction,
  exportMenuProgramId,
  exportMenuTypes,
  onRowMenuOpenChange,
  rowsPerPage,
  rowsPerPageOptions,
  onRowsPerPageChange,
  page,
  totalPages,
  onPageChange,
  activeDialog,
  onDialogOpenChange,
  publishVisibility,
  onPublishVisibilityChange,
  onConfirmPublish,
  onConfirmEnd,
  onConfirmCancel,
  publishError,
  publishIncompleteJobs,
  isCheckingPublishReadiness,
}: ProgramsPageProps) {
  return (
    <>
      <ConsultantLayout activeMenuId="programs">
        <ConsultantLayout.Header>
          <ConsultantListHeader
            title={
              <ConsultantPageTitle icon={<ArchiveIcon className="size-4" />} brand={brand}>
                Programas
              </ConsultantPageTitle>
            }
            actions={
              <Button onClick={onCreateProgram} data-testid="programs-list-create">
                <PlusIcon />
                Criar programa
              </Button>
            }
            tools={
              <>
                <ConsultantSearchInput
                  value={searchQuery}
                  onChange={onSearchChange}
                  ariaLabel="Buscar programas"
                  placeholder="Busque por ID ou nome"
                  data-testid="programs-list-search"
                />
                <ConsultantSortDropdown
                  ariaLabel="Ordenar programas"
                  activeAriaLabel={
                    selectedSortLabel ? `Ordenação atual: ${selectedSortLabel}` : undefined
                  }
                  options={sortOptions.map<ConsultantSortOption>((o) => ({
                    id: o.id,
                    label: o.label,
                    isActive: o.isSelected,
                  }))}
                  isActive={isSortSelected}
                  onSelect={onSelectSort}
                  onClear={onClearSort}
                  data-testid="programs-list-sort"
                />
              </>
            }
          />
        </ConsultantLayout.Header>

        <ConsultantLayout.Body>
          {isInitialLoading ? (
            <StatusPanel
              icon={<LoaderCircleIcon className="size-7 animate-spin" />}
              title="Buscando programas..."
            />
          ) : isReordering ? (
            <StatusPanel
              icon={<LoaderCircleIcon className="size-7 animate-spin" />}
              title="Reordenando programas..."
            />
          ) : programs.length === 0 ? (
            <EmptyState onCreateProgram={onCreateProgram} />
          ) : (
            <ConsultantTableSection
              ariaLabel="Lista de programas"
              pagination={
                <ConsultantTablePagination
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={rowsPerPageOptions}
                  onRowsPerPageChange={onRowsPerPageChange}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  data-testid="programs-list-pagination"
                />
              }
            >
              <div className="flex min-h-0 flex-1 flex-col">
                <Table
                  className="table-fixed"
                  containerClassName="flex-1 overflow-auto"
                  storageId="recruiter:programs-list"
                >
                  <TableHeader className="sticky top-0 z-10 border-b border-border bg-card">
                    <TableRow className="hover:bg-transparent">
                      <TableHead resizable colKey="id" className="w-[80px]">
                        ID
                      </TableHead>
                      <TableHead resizable colKey="name">
                        Nome
                      </TableHead>
                      <TableHead resizable colKey="status">
                        Status
                      </TableHead>
                      <TableHead resizable colKey="jobs">
                        Vagas
                      </TableHead>
                      <TableHead resizable colKey="registrations">
                        Inscritos
                      </TableHead>
                      <TableHead resizable colKey="start">
                        Início
                      </TableHead>
                      <TableHead resizable colKey="end">
                        Fim
                      </TableHead>
                      <TableHead className="w-12">
                        <span className="sr-only">Ações</span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {programs.map((program) => (
                      <ProgramRow
                        key={program.id}
                        program={program}
                        onOpen={() => onOpenProgram(program.id)}
                        onAction={(action) => onProgramAction(action, program.id)}
                        exportTypes={
                          exportMenuProgramId === program.id ? exportMenuTypes : undefined
                        }
                        onMenuOpenChange={(open) => onRowMenuOpenChange(program.id, open)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            </ConsultantTableSection>
          )}
        </ConsultantLayout.Body>
      </ConsultantLayout>

      <ProgramPublishDialog
        open={activeDialog === 'publish'}
        onOpenChange={onDialogOpenChange}
        visibility={publishVisibility}
        onVisibilityChange={onPublishVisibilityChange}
        onConfirm={onConfirmPublish}
        error={publishError}
        incompleteJobs={publishIncompleteJobs}
        isCheckingReadiness={isCheckingPublishReadiness}
      />
      <ProgramConfirmDialog
        open={activeDialog === 'end'}
        onOpenChange={onDialogOpenChange}
        title="Encerrar"
        description="Deseja encerrar essa oportunidade? Ela deixará de receber novas candidaturas e não poderá ser reaberta."
        cancelLabel="Voltar"
        confirmLabel="Encerrar"
        onConfirm={onConfirmEnd}
      />
      <ProgramConfirmDialog
        open={activeDialog === 'cancel'}
        onOpenChange={onDialogOpenChange}
        title="Cancelar"
        description="Deseja cancelar essa oportunidade? Ela deixará de receber novas candidaturas e não poderá ser reaberta."
        cancelLabel="Voltar"
        confirmLabel="Fazer cancelamento"
        onConfirm={onConfirmCancel}
      />
    </>
  );
}

/**
 * EU-241 — company logo cell avatar for the programs listing. 28×28 rounded
 * square; falls back to a program icon placeholder when the company has no
 * logo. Mirrors the JobHeaderLogo pattern used across the recruiter app.
 */
function ProgramCompanyLogo({ logoUrl }: { logoUrl: string | null }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt="Logo da empresa"
        loading="lazy"
        className="size-7 shrink-0 rounded-md object-cover"
      />
    );
  }
  return (
    <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
      <ArchiveIcon className="size-4" />
    </div>
  );
}

interface ProgramRowProps {
  program: Program;
  onOpen: () => void;
  onAction: (action: ProgramAction) => void;
  /** EU-654 — tipos ofertáveis quando o menu DESTA linha está aberto. */
  exportTypes: ProgramBaseTypeOption[] | undefined;
  onMenuOpenChange: (open: boolean) => void;
}

function ProgramRow({ program, onOpen, onAction, exportTypes, onMenuOpenChange }: ProgramRowProps) {
  // EU-570 — o título é clicável em TODOS os status: programas mutáveis abrem
  // o edit normal; Encerrado/Cancelado abrem o mesmo fluxo em modo somente
  // visualização (campos bloqueados + tooltip "Edição bloqueada"). Substitui a
  // regra antiga (RN-01) que renderizava um <span> não interativo.
  return (
    <TableRow>
      <TableCell>
        <ConsultantCopyableId value={program.id} label="ID do programa" />
      </TableCell>
      <TableCell>
        <div className="flex flex-row items-center gap-2">
          <ProgramCompanyLogo logoUrl={program.logoUrl} />
          <button
            type="button"
            onClick={onOpen}
            className="text-sm font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
          >
            {program.name}
          </button>
        </div>
      </TableCell>
      <TableCell>
        <StatusBadge status={program.status} />
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {program.jobsCount.toLocaleString('pt-BR')}
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {formatProgramRegistrations(program.status, program.registrationsCount)}
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">
        {program.startDate}
        {program.daysRemaining != null && (
          <span className="text-muted-foreground"> · {program.daysRemaining} dias</span>
        )}
      </TableCell>
      <TableCell className="tabular-nums text-muted-foreground">{program.endDate}</TableCell>
      <TableCell className="text-right">
        <ProgramRowActions
          program={program}
          onAction={onAction}
          exportTypes={exportTypes}
          onMenuOpenChange={onMenuOpenChange}
        />
      </TableCell>
    </TableRow>
  );
}

interface ProgramRowActionsProps {
  program: Program;
  onAction: (action: ProgramAction) => void;
  exportTypes: ProgramBaseTypeOption[] | undefined;
  onMenuOpenChange: (open: boolean) => void;
}

function ProgramRowActions({
  program,
  onAction,
  exportTypes,
  onMenuOpenChange,
}: ProgramRowActionsProps) {
  const groups = getMenuGroupsForStatus(program.status);
  return (
    <DropdownMenu onOpenChange={onMenuOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Ações para ${program.name}`}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-border/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 data-[state=open]:bg-border/50 data-[state=open]:text-foreground"
          data-testid={`programs-list-row-actions-${program.id}`}
        >
          <MoreHorizontalIcon className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Épico EU-561 — "Extrair bases" vem primeiro e aparece em qualquer
            status: extrair a base de um programa encerrado é justamente quando
            o consultor analisa. EU-654 — a lista é DINÂMICA (RN-02/03): vem do
            backend quando o menu abre; enquanto carrega, item desabilitado. */}
        <DropdownMenuLabel>Extrair bases</DropdownMenuLabel>
        <DropdownMenuGroup>
          {exportTypes === undefined ? (
            <DropdownMenuItem disabled data-testid={`programs-list-export-loading-${program.id}`}>
              Carregando tipos…
            </DropdownMenuItem>
          ) : (
            getExportGroup(exportTypes).map((option) => (
              <DropdownMenuItem
                key={option.action}
                onClick={() => onAction(option.action)}
                data-testid={`programs-list-${option.action}-${program.id}`}
              >
                {option.label}
              </DropdownMenuItem>
            ))
          )}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Gerenciar programa</DropdownMenuLabel>
        {groups.map((group, groupIndex) => (
          <Fragment key={groupIndex}>
            {groupIndex > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              {group.map((option) => (
                <DropdownMenuItem
                  key={option.action}
                  destructive={option.destructive}
                  onClick={() => onAction(option.action)}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StatusBadge({ status }: { status: ProgramStatus }) {
  if (status === 'ativa') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Ativa
      </Badge>
    );
  }
  if (status === 'ativo-privado') {
    return (
      <Badge className="bg-secondary text-primary" leftIcon={<RadioIcon className="size-3" />}>
        Ativo e priv.
      </Badge>
    );
  }
  if (status === 'rascunho') {
    return (
      <Badge
        className="bg-warning text-primary-foreground"
        leftIcon={<FilePenIcon className="size-3" />}
      >
        Rascunho
      </Badge>
    );
  }
  if (status === 'encerrado') {
    // "Encerrada" (feminine) per Figma — refers to "oportunidade", not "programa".
    return (
      <Badge
        className="bg-primary text-primary-foreground"
        leftIcon={<FlagIcon className="size-3" />}
      >
        Encerrada
      </Badge>
    );
  }
  return (
    <Badge
      className="bg-destructive text-primary-foreground"
      leftIcon={<BanIcon className="size-3" />}
    >
      Cancelado
    </Badge>
  );
}

function StatusPanel({
  icon,
  title,
  bordered = true,
}: {
  icon: React.ReactNode;
  title: string;
  /** When true (default), renders the dashed-border filling card. When false,
   *  renders an unbordered centered block — used when StatusPanel is itself
   *  nested inside a bordered section (the reordering case). */
  bordered?: boolean;
}) {
  const inner = (
    <>
      <div className="flex size-16 items-center justify-center rounded-full bg-muted text-foreground">
        {icon}
      </div>
      <p className="text-2xl font-medium tracking-tight text-foreground">{title}</p>
    </>
  );
  if (!bordered) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-5 text-center">
        {inner}
      </div>
    );
  }
  return <ConsultantListPlaceholder>{inner}</ConsultantListPlaceholder>;
}

function EmptyState({ onCreateProgram }: { onCreateProgram: () => void }) {
  return (
    <ConsultantListPlaceholder>
      <div className="flex flex-col items-center gap-5">
        <div className="flex size-16 items-center justify-center rounded-full bg-muted text-foreground">
          <TriangleAlertIcon className="size-7" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <p className="text-2xl font-medium tracking-tight text-foreground">Sem programas</p>
          <p className="text-base text-muted-foreground">
            No momento, ainda não temos nenhum programa cadastrado nessa Área de Trabalho. Cadastre
            o seu e comece a criar e publicar oportunidades.
          </p>
        </div>
      </div>
      <Button onClick={onCreateProgram}>Criar programa</Button>
    </ConsultantListPlaceholder>
  );
}
